import React from 'react';

import {
	ButtonMenuItem,
	type ButtonMenuItemProps,
} from '@atlaskit/navigation-system/side-nav-items/button-menu-item';
import type { GlobalShortcutsItem } from '@atlassian/global-shortcuts';

import type {
	ButtonItemConfig,
	ExpandableItemConfig,
	FlyoutItemConfig,
	LinkItemConfig,
	NavItemConfig,
} from '../../../common/types';
import NavItem from '../../../common/ui/nav-item';

export interface MoreMenuItemProps {
	id: string;
	items: Record<string, NavItemConfig>;
	shortcutItems?: Record<string, GlobalShortcutsItem>;
	onClick?: () => void;
	onPeek?: (id: string) => void;
}

export default function MoreMenuItem({
	id,
	items,
	shortcutItems,
	onClick,
	onPeek,
}: MoreMenuItemProps): React.ReactNode {
	const shortcutItem = shortcutItems?.[id];
	if (shortcutItem) {
		return (
			// TODO: Should the global-shortcuts package handle rendering a more-menu version of the shortcut item?
			// Or should we handle that here?
			<ButtonMenuItem>Shortcut item {id}</ButtonMenuItem>
		);
	}

	// Check if the item is in the items map
	const item = items[id];

	if (!item) {
		throw new Error(
			`NavItem: No item found for id "${id}". Ensure the item is defined in appItems or globalItems.`,
		);
	}

	if (item.type === 'custom') {
		// If the item is a custom item, render it directly
		return <item.Component onPeek={() => onPeek?.(id)} isInMoreMenu={true} onClick={onClick} />;
	}

	const moreItem = getPropsForItem({ id, item, onClick, onPeek });
	return <NavItem item={moreItem} />;
}

function getPropsForItem({
	id,
	item,
	onClick,
	onPeek,
}: {
	id: string;
	item: NavItemConfig;
	onClick?: () => void;
	onPeek?: (id: string) => void;
}): NavItemConfig {
	if (item.type === 'expand' || item.type === 'flyout') {
		return getMorePropsForExpandFlyout({ id, item, onClick, onPeek });
	} else if (item.type === 'button') {
		return getMorePropsForButton({ item, onClick });
	} else if (item.type === 'link') {
		return getMorePropsForLink({ item, onClick });
	}

	// If the item is not a button, link, expandable, or flyout, return as is
	return item;
}

function getMorePropsForExpandFlyout({
	id,
	item,
	onClick,
	onPeek,
}: {
	id: string;
	item: ExpandableItemConfig | FlyoutItemConfig;
	onClick?: () => void;
	onPeek?: (id: string) => void;
}): NavItemConfig {
	const { type, children, ...rest } = item;
	const originalProps = item as ButtonMenuItemProps;
	const moreRest: Omit<ButtonMenuItemProps, 'children'> = {
		elemBefore: rest.elemBefore,
		onClick: (...args) => {
			originalProps.onClick?.(...args);
			onClick?.();
			onPeek?.(id);
		},
	};
	// triggerLabel is available on both flyout and expandable items
	const moreChildren = item.triggerLabel;

	return {
		...moreRest,
		// If the item is in the more menu and is an expandable or flyout item, render as a button with peek
		type: 'button',
		children: moreChildren,
	};
}

function getMorePropsForButton({
	item,
	onClick,
}: {
	item: ButtonItemConfig;
	onClick?: () => void;
}): NavItemConfig {
	const { onClick: itemOnClick, ...rest } = item;
	// Remove props that are not appropriate for the more menu
	const moreRest: Pick<ButtonItemConfig, 'actions' | 'actionsOnHover' | 'elemAfter' | 'onClick'> = {
		actions: undefined,
		actionsOnHover: undefined,
		elemAfter: undefined,
		onClick: (...args) => {
			itemOnClick?.(...args);
			onClick?.();
		},
	};
	return {
		...rest,
		...moreRest,
	};
}

function getMorePropsForLink({
	item,
	onClick,
}: {
	item: LinkItemConfig;
	onClick?: () => void;
}): NavItemConfig {
	const { onClick: itemOnClick, ...rest } = item;
	// Remove props that are not appropriate for the more menu
	const moreRest: Pick<LinkItemConfig, 'actions' | 'actionsOnHover' | 'elemAfter' | 'onClick'> = {
		actions: undefined,
		actionsOnHover: undefined,
		elemAfter: undefined,
		onClick: (...args) => {
			itemOnClick?.(...args);
			onClick?.();
		},
	};
	return {
		...rest,
		...moreRest,
	};
}
